import { Badge } from '@/components/ui/badge';
import { Pencil, Loader2 } from 'lucide-react';
import type { InvoiceStatus } from '@/components/forms/InvoiceStatusSelect';
import { cn } from '@/lib/utils';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  className?: string;
  editable?: boolean;  // ✅ NEW: Show pencil icon and hover styles
  amountPaid?: number;  // V168: Used to render "$X of $Y" subtext on partially_paid badges
  totalAmount?: number; // V168: ditto
  // v0.14.4 R5 (silent-loss kill): when an inline status PUT is in flight for this
  // row, render a spinner instead of the pencil so the optimistic value never looks
  // like a settled fait accompli. If the PUT is rejected the badge rolls back; the
  // spinner makes the in-flight state visible while that resolves.
  pending?: boolean;
}

// Map invoice status values to human-readable labels
const invoiceStatusLabels: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  ready: 'Ready',
  sent: 'Sent',
  paid: 'Paid',
  partially_paid: 'Partially Paid',
  cancelled: 'Cancelled',
};

// Map invoice status to badge styling
// Christa feedback 2026-02-03: Color-code for quick recognition
function getInvoiceStatusStyles(status: InvoiceStatus): { variant: 'default' | 'secondary' | 'outline' | 'destructive'; className?: string } {
  switch (status) {
    case 'draft':
      return { variant: 'outline' }; // White/Gray
    case 'ready':
      return { variant: 'secondary', className: 'bg-purple-100 text-purple-800 hover:bg-purple-200' }; // Light Purple
    case 'sent':
      return { variant: 'secondary', className: 'bg-blue-100 text-blue-800 hover:bg-blue-200' }; // Blue
    case 'partially_paid':
      // V168: Amber — money received but invoice not yet closed out.
      return { variant: 'secondary', className: 'bg-amber-100 text-amber-800 hover:bg-amber-200' };
    case 'paid':
      return { variant: 'default', className: 'bg-green-600 text-white hover:bg-green-700' }; // Green
    case 'cancelled':
      return { variant: 'destructive' }; // Red
    default:
      return { variant: 'outline' };
  }
}

// V168: Format "$X of $Y" subtext when we have payment data on a partially_paid badge.
function formatPaymentSubtext(amountPaid?: number, totalAmount?: number): string | null {
  if (amountPaid === undefined || totalAmount === undefined) return null;
  const fmt = (n: number) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(n);
  return `${fmt(amountPaid)} of ${fmt(totalAmount)}`;
}

/**
 * Invoice Status Badge Component
 *
 * Phase 6 Batch 5.3: Inline editing pattern consistency fix
 * Phase 6 Fix: Enhanced with editable prop for better UX
 * Reference: ProjectStageBadge component
 *
 * Purpose:
 * - Displays formatted invoice status labels (e.g., "Draft" not "draft")
 * - Matches dropdown labels for visual consistency
 * - Provides proper badge variants for different statuses
 * - Shows pencil icon and hover styles when editable
 *
 * Why this exists:
 * - Before: Badge showed raw status value "draft" but dropdown showed "Draft"
 * - After: Both badge and dropdown show "Draft" (consistent)
 * - Pattern matches ProjectStageBadge approach
 * - Editable variant makes it clear the status can be changed
 */
export function InvoiceStatusBadge({
  status,
  className,
  editable = false,
  amountPaid,
  totalAmount,
  pending = false,
}: InvoiceStatusBadgeProps) {
  const styles = getInvoiceStatusStyles(status);
  // V168: For partially_paid, show "$X of $Y" via native title tooltip + aria-label
  // so Christa sees the actual numbers on hover and screen readers / Playwright can
  // detect it. Also rendered inline (small muted text) so it's visible without hover.
  const subtext = status === 'partially_paid'
    ? formatPaymentSubtext(amountPaid, totalAmount)
    : null;
  const tooltip = subtext ? `${subtext} paid` : undefined;
  // Polish R2 (Dmitri round 2 — A11y): screen readers were silent on status
  // badges; nothing announced "Sent" / "Paid" beyond the visible color cue.
  // Anchor an aria-label like "Invoice status: Sent" on the badge container
  // and mark it role="status" so it's surfaced as a live region. When we
  // already have partially-paid subtext, combine the two into one label.
  const baseStatusLabel = `Invoice status: ${invoiceStatusLabels[status]}`;
  // v0.14.4 R5: announce the in-flight state to screen readers / Playwright too.
  const ariaLabel = pending
    ? `${baseStatusLabel} (saving…)`
    : tooltip ? `${baseStatusLabel} (${tooltip})` : baseStatusLabel;
  return (
    <div
      role="status"
      aria-busy={pending || undefined}
      className={cn(
        'inline-flex items-center gap-2',
        editable && 'cursor-pointer hover:opacity-80 transition-opacity'
      )}
      title={tooltip}
      aria-label={ariaLabel}
      data-testid={status === 'partially_paid' ? 'invoice-status-badge-partially-paid' : undefined}
    >
      <Badge variant={styles.variant} className={cn(styles.className, className)}>
        {invoiceStatusLabels[status]}
      </Badge>
      {subtext && (
        <span className="text-xs text-muted-foreground tabular-nums" data-testid="partial-payment-subtext">
          {subtext}
        </span>
      )}
      {/* v0.14.4 R5: spinner while the inline status PUT is in flight; falls back
          to the pencil affordance once settled. */}
      {pending ? (
        <Loader2
          className="h-4 w-4 text-muted-foreground animate-spin"
          data-testid="invoice-status-pending-spinner"
          aria-hidden="true"
        />
      ) : (
        editable && <Pencil className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  );
}
